import moment from 'moment'
import download from 'downloadjs'

import {
  SORT_TYPES,
  REPORTS_TABLE_COLUMN_HEADERS,
  enrollmentConnectionStatusesOrderLookupTable,
  KARDIA_PRO_START_DATE,
  KARDIA_PRO_END_DATE,
  ESIGN_TYPES,
  EXPORT_TYPES,
  getDateFormat,
} from '../../../constants'
import {
  getPatientName,
  getName,
  StandardCompare,
} from '../../../containers/Utilities'
import { comparePatients } from '../../../store/appReducer'

export const sortReportIds = (
  sorts = {
    sortBy: REPORTS_TABLE_COLUMN_HEADERS.END_DATE,
    sortDirection: SORT_TYPES.DESC,
  },
  rIds = [],
  reportByIds = {},
  patientByIds = {},
  prescriptionByIds = {},
  physicianByIds = {},
  billingCodeByIds = {},
) => {
  const {
    sortBy,
    sortDirection,
  } = sorts
  const reportIds = [ ...rIds ]
  let sortedReportIds = reportIds

  if (reportIds) {
    switch (sortBy) {
    case REPORTS_TABLE_COLUMN_HEADERS.END_DATE:
      sortedReportIds = reportIds.sort((a, b) => StandardCompare.dates(reportByIds[a].endAt, reportByIds[b].endAt, sortDirection))
      break
    case REPORTS_TABLE_COLUMN_HEADERS.PATIENT:
      sortedReportIds = reportIds.sort((a, b) => {
        const report1 = reportByIds[a]
        const report2 = reportByIds[b]
        const patient1 = patientByIds[report1.participantId]
        const patient2 = patientByIds[report2.participantId]
        return comparePatients(patient1, patient2, sortDirection)
      })
      break
    case REPORTS_TABLE_COLUMN_HEADERS.PHYSICIAN:
      sortedReportIds = reportIds.sort((a, b) => {
        const report1 = reportByIds[a]
        const report2 = reportByIds[b]
        const physician1 = physicianByIds[report1.physicianMemberId]
        const physician2 = physicianByIds[report2.physicianMemberId]
        const p1Name = report1.physicianMemberId ? getName(physician1.firstName, physician1.lastName, '') : ''
        const p2Name = report2.physicianMemberId ? getName(physician2.firstName, physician2.lastName, '') : ''

        return StandardCompare.strings(p1Name, p2Name, sortDirection)
      })
      break
    case REPORTS_TABLE_COLUMN_HEADERS.PRESCRIPTION:
      sortedReportIds = reportIds.sort((a, b) => {
        const report1 = reportByIds[a]
        const report2 = reportByIds[b]
        const prescription1 = prescriptionByIds[report1.connectionTemplateId]
        const prescription2 = prescriptionByIds[report2.connectionTemplateId]
        const p1Name = prescription1.name || ''
        const p2Name = prescription2.name || ''

        const result = StandardCompare.strings(p1Name, p2Name, sortDirection)
        if (result === 0) {
          const patient1 = patientByIds[report1.participantId]
          const patient2 = patientByIds[report2.participantId]

          return comparePatients(patient1, patient2, sortDirection)
        } else {
          return result
        }
      })
      break
    case REPORTS_TABLE_COLUMN_HEADERS.BILLING_CODE:
      sortedReportIds = reportIds.sort((a, b) => {
        const report1 = reportByIds[a]
        const report2 = reportByIds[b]
        const billingCode1 = (billingCodeByIds[report1.billingCodeId] && billingCodeByIds[report1.billingCodeId].name) || ''
        const billingCode2 = (billingCodeByIds[report1.billingCodeId] && billingCodeByIds[report2.billingCodeId].name) || ''

        return StandardCompare.strings(billingCode1, billingCode2, sortDirection)
      })
      break
    case REPORTS_TABLE_COLUMN_HEADERS.ESIGNED:
      sortedReportIds = reportIds.sort((a, b) => {
        const report1 = reportByIds[a]
        const report2 = reportByIds[b]

        return StandardCompare.dates(report1.esignedAt, report2.esignedAt, sortDirection)
      })
      break
    case REPORTS_TABLE_COLUMN_HEADERS.EXPORTED:
      sortedReportIds = reportIds.sort((a, b) => {
        const report1 = reportByIds[a]
        const report2 = reportByIds[b]

        return StandardCompare.dates(report1.exportedAt, report2.exportedAt, sortDirection)
      })
      break
    default:
      // No sort, keep previous sort
      break
    }
  }

  return sortedReportIds
}

export const filterReportIds = (
  filters = {
    selectedStartDate: undefined,
    selectedEndDate: undefined,
    selectedPatientId: undefined,
    selectedPhysicianId: undefined,
    selectedPhysicians: undefined,
    selectedPrescriptionId: undefined,
    selectedBillingCode: undefined,
    selectedEsignedId: undefined,
    selectedExportedId: undefined,
  },
  reportIds = [],
  reportByIds = {},
  billingCodeByIds = {},
) => {
  const {
    selectedStartDate,
    selectedEndDate,
    selectedPatientId,
    selectedPhysicianId,
    selectedPhysicians,
    selectedPrescriptionId,
    selectedBillingCode,
    selectedEsignedId,
    selectedExportedId,
  } = filters
  let filteredReportIds = reportIds

  if (reportIds) {
    if (selectedStartDate !== KARDIA_PRO_START_DATE
      || selectedEndDate !== KARDIA_PRO_END_DATE
    ) {
      filteredReportIds = filteredReportIds
        .filter(id => {
          const r = reportByIds[id]
          if (moment(r.endAt).isBefore(moment(selectedStartDate).startOf('day'))
            || moment(r.endAt).isAfter(moment(selectedEndDate).endOf('day'))) {
            return false
          }

          return true
        })
    }

    if (selectedPatientId) {
      filteredReportIds = filteredReportIds
        .filter(id => {
          const r = reportByIds[id]
          return r.participantId === selectedPatientId
        })
    }

    if (selectedPhysicianId) {
      filteredReportIds = filteredReportIds
        .filter(id => {
          const r = reportByIds[id]
          return r.physicianMemberId === selectedPhysicianId
        })
    }

    if (selectedPhysicians && selectedPhysicians.length > 0) {
      let hasNone = false
      const selectedPhysiciansById = selectedPhysicians
        .filter(p => {
          if (p.id === '0') {
            hasNone = true
          }
          return p.id !== '0'
        })
        .map(p => p.id)

      filteredReportIds = filteredReportIds
        .filter(id => {
          const recording = reportByIds[id]

          if (hasNone && !recording.physicianMemberId) {
            return true
          } else if (recording.physicianMemberId) {
            return selectedPhysiciansById.includes(recording.physicianMemberId)
          }
          return false
        })
    }

    if (selectedPrescriptionId) {
      filteredReportIds = filteredReportIds
        .filter(id => {
          const r = reportByIds[id]
          return r.connectionTemplateId === selectedPrescriptionId
        })
    }

    if (selectedBillingCode.length > 0) {
      filteredReportIds = filteredReportIds
        .filter(id => {
          const r = reportByIds[id]
          return selectedBillingCode.some(billingCode => billingCode.id === r.billingCodeId)
        })
    }


    if (selectedEsignedId) {
      filteredReportIds = filteredReportIds
        .filter(id => {
          const r = reportByIds[id]

          switch (selectedEsignedId) {
          case ESIGN_TYPES.NOT_ESIGNED:
            return r.esignedAt === null
          case ESIGN_TYPES.ESIGNED:
            return r.esignedAt !== null
          case ESIGN_TYPES.ALL_ESIGN:
          default:
            return true
          }
        })
    }

    if (selectedExportedId) {
      filteredReportIds = filteredReportIds
        .filter(id => {
          const r = reportByIds[id]

          switch (selectedExportedId) {
          case EXPORT_TYPES.NOT_EXPORTED:
            return r.exportedAt === null
          case EXPORT_TYPES.EXPORTED:
            return r.exportedAt !== null
          case EXPORT_TYPES.ALL_EXPORT:
          default:
            return true
          }
        })
    }
  }

  return filteredReportIds
}


export const downloadBillingList = (selectedIds, reports, patients, physicians, billingCodes) =>
  Promise.resolve()
    .then(() => {
      const exportDateFormat = getDateFormat()
      const csvData = [
        [
          'Report Start Date',
          'Report End Date',
          'Patient Name',
          'Patient MRN',
          'Patient DOB',
          'Ordering Provider Name',
          'ICD-10 Code',
          'Billing Code',
          'E-signed',
          'Exported',
        ],
      ]
      if (selectedIds.length > 0 && reports && patients && physicians && billingCodes) {
        selectedIds
          .forEach(id => {
            const r = reports.data.byId[id]
            const p = patients.data.byId[r.participantId] || {}
            const oP = physicians.data.byId[r.physicianMemberId] || {}
            const bC = billingCodes.data.byId[r.billingCodeId].name || ''
            const dob = p.dob !== null ? moment.utc(p.dob).format(exportDateFormat) : ''
            csvData.push([
              `"${moment.utc(r.startAt).format(exportDateFormat)}\t"`,
              `"${moment.utc(r.endAt).format(exportDateFormat)}\t"`,
              `"${getPatientName(p.firstName, p.lastName, '')}"`,
              `"${p.customParticipantID !== null ? p.customParticipantID : ''}"`,
              `"${dob}\t"`,
              `"${getName(oP.firstName, oP.lastName, '')}"`,
              `"${r.icd10CodeId !== null ? r.icd10CodeId : ''}"`,
              `"${bC}"`,
              (r.esignedAt !== null) ? 'Yes' : 'No',
              (r.exportedAt !== null) ? 'Yes' : 'No',
            ])
          })
      }

      const csvLineArray = []
      csvData.forEach((dataRow, index) => {
        const line = dataRow.join(',')
        csvLineArray.push(line)
      })
      const csvContent = csvLineArray.join('\n')

      /* eslint-disable no-undef */
      const blob = new Blob([ csvContent ], { type: 'data:text/csv;separator:\',\';charset=utf-8' })

      // console.log(reports, selectedIds, csvContent)
      return download(blob, `reports_billing_list_${moment().format(exportDateFormat)}.csv`, 'text/csv')
    })



// WEBPACK FOOTER //
// ./src/legacy/containers/Tables/ReportTable/Utilities.js